import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ApplicationEnvironmentRole, CompanyProductDeployConfiguration } from 'src/app/models/company_product_deploy_configuration.model';
import { MessagingConversation } from 'src/app/models/messaging_conversation.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserService } from 'src/app/services/user.service';

export interface CompanyProductDeployConfigurationDialogData {
  companyProductId: string;
  companyId: number;
  messagingConversations: MessagingConversation[];
  internalTeamMessagingConversation: MessagingConversation | null;
}

@Component({
  selector: 'app-company-product-deploy-configuration-dialog',
  templateUrl: './company-product-deploy-configuration-dialog.component.html',
  styleUrls: ['./company-product-deploy-configuration-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatButton,
    MatIconButton,
    MatIcon,
    MatSelect,
    MatOption,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatCheckbox,
  ],
})
export class CompanyProductDeployConfigurationDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CompanyProductDeployConfigurationDialogComponent>>(MatDialogRef);
  data = inject<CompanyProductDeployConfigurationDialogData>(MAT_DIALOG_DATA);
  private fb = inject(UntypedFormBuilder);
  private api = inject(ApiService);
  private userService = inject(UserService);
  private permissions = inject(PermissionsService);
  private cdr = inject(ChangeDetectorRef);

  form: UntypedFormGroup;
  configurations: CompanyProductDeployConfiguration[] = [];
  showForm = false;
  editingConfiguration: CompanyProductDeployConfiguration | null = null;
  userAutocompleteControl = new UntypedFormControl();
  filteredUsers: Observable<User[]> = of([]);
  selectedUser: User | null = null;
  selectedNotifiableTypeValue = '';
  emailChannelControl = new UntypedFormControl(false);
  slackChannelControl = new UntypedFormControl(false);
  teamsChannelControl = new UntypedFormControl(false);

  readonly environment_role_labels: Record<ApplicationEnvironmentRole, string> = {
    production: 'Producción',
    staging: 'Staging',
    development: 'Desarrollo',
    other: 'Otro',
  };

  constructor() {
    this.form = this.fb.group({
      role: ['', Validators.required],
      notifiable_type: ['', Validators.required],
      notifiable_id: ['', Validators.required],
      notify_on_success: [false],
      notify_on_failure: [false],
      notification_channels: [[], this.validateNotificationChannels.bind(this)],
      only_notify_if_has_mapped_tasks: [false],
      include_tasks_in_messages: [false],
      include_task_assignees_in_messages: [false],
      include_company_product_name_in_messages: [false],
    });
  }

  ngOnInit(): void {
    this.loadConfigurations();
    this.setupUserAutocomplete();
    this.listenUserSelection();
    this.listenNotifiableTypeChanges();
    this.setupChannelControls();
  }

  get internal_team_conversation(): MessagingConversation | null {
    return this.data.internalTeamMessagingConversation;
  }

  get additional_configurations(): CompanyProductDeployConfiguration[] {
    const internal_id = this.internal_team_conversation?.id;

    return this.configurations.filter(config => {
      if (internal_id == null) {
        return true;
      }

      return !(config.notifiable_type === 'App\\Models\\MessagingConversation' && config.notifiable_id === internal_id);
    });
  }

  get availableMessagingConversations(): MessagingConversation[] {
    const internal_id = this.internal_team_conversation?.id;

    return (this.data.messagingConversations || []).filter(conversation => conversation.id !== internal_id);
  }

  displayUserFn(user: User): string {
    return user ? user.full_name : '';
  }

  getNotifiableName(config: CompanyProductDeployConfiguration): string {
    if (!config.notifiable) {
      return 'Desconocido';
    }

    if (config.notifiable_type === 'App\\Models\\User') {
      const user = config.notifiable as User;

      return user.full_name || user.name || 'Usuario';
    }

    const conversation = config.notifiable as MessagingConversation;

    return conversation.display_name ?? 'Conversación';
  }

  getEnvironmentName(config: CompanyProductDeployConfiguration): string {
    if (!config.role) {
      return '-';
    }

    return this.environment_role_labels[config.role] || config.role;
  }

  getChannelsDisplay(config: CompanyProductDeployConfiguration): string {
    if (config.notifiable_type === 'App\\Models\\MessagingConversation' && config.notifiable) {
      const conversation = config.notifiable as MessagingConversation;

      return conversation.provider === 'google_chat' ? 'Google Chat' : conversation.provider === 'teams' ? 'Teams' : 'Slack';
    }

    if (config.notification_channels && config.notification_channels.length > 0) {
      return config.notification_channels.map(channel => channel.toUpperCase()).join(', ');
    }

    return '-';
  }

  getNotifyEventsLabel(config: CompanyProductDeployConfiguration): string {
    const events: string[] = [];
    if (config.notify_on_success) {
      events.push('Éxito');
    }
    if (config.notify_on_failure) {
      events.push('Fallo');
    }

    return events.length > 0 ? events.join(' · ') : 'Ninguno';
  }

  isUserType(): boolean {
    const normalized = this.selectedNotifiableTypeValue.replace(/\\\\/g, '\\');

    return normalized === 'App\\Models\\User' || normalized === 'App\Models\User';
  }

  isMessagingConversationType(): boolean {
    const normalized = this.selectedNotifiableTypeValue.replace(/\\\\/g, '\\');

    return normalized === 'App\\Models\\MessagingConversation' || normalized === 'App\Models\MessagingConversation';
  }

  close(): void {
    this.dialogRef.close(true);
  }

  showCreateForm(): void {
    this.editingConfiguration = null;
    this.selectedUser = null;
    this.userAutocompleteControl.setValue('');
    this.selectedNotifiableTypeValue = '';
    this.form.reset();
    this.form.patchValue({
      notify_on_success: false,
      notify_on_failure: false,
      notification_channels: [],
      only_notify_if_has_mapped_tasks: false,
      include_tasks_in_messages: false,
      include_task_assignees_in_messages: false,
      include_company_product_name_in_messages: false,
    });
    this.showForm = true;
    this.cdr.markForCheck();
  }

  showEditForm(config: CompanyProductDeployConfiguration): void {
    this.editingConfiguration = config;

    const normalizedType = config.notifiable_type ? config.notifiable_type.replace(/\\\\/g, '\\') : '';
    this.selectedNotifiableTypeValue = normalizedType;

    if (normalizedType === 'App\\Models\\User' && config.notifiable) {
      const user = config.notifiable as User;
      this.selectedUser = user;
      this.userAutocompleteControl.setValue(user);
    } else {
      this.selectedUser = null;
      this.userAutocompleteControl.setValue('');
    }

    this.form.patchValue({
      role: config.role,
      notifiable_type: config.notifiable_type,
      notifiable_id: config.notifiable_id,
      notify_on_success: config.notify_on_success,
      notify_on_failure: config.notify_on_failure,
      notification_channels: config.notification_channels || [],
      only_notify_if_has_mapped_tasks: config.only_notify_if_has_mapped_tasks ?? false,
      include_tasks_in_messages: config.include_tasks_in_messages ?? false,
      include_task_assignees_in_messages: config.include_task_assignees_in_messages ?? false,
      include_company_product_name_in_messages: config.include_company_product_name_in_messages,
    });

    const channelsControl = this.form.get('notification_channels');
    if (channelsControl) {
      channelsControl.updateValueAndValidity();
    }

    this.showForm = true;
    this.cdr.markForCheck();
  }

  cancelForm(): void {
    this.showForm = false;
    this.editingConfiguration = null;
    this.selectedUser = null;
    this.userAutocompleteControl.setValue('');
    this.selectedNotifiableTypeValue = '';
    this.form.reset();
    this.cdr.markForCheck();
  }

  submit(): void {
    if (!this.form.valid) {
      return;
    }

    const formValue = this.form.value;
    const body: Record<string, unknown> = {
      ...formValue,
      company_product_id: this.data.companyProductId,
    };

    if (this.isMessagingConversationType()) {
      body['notification_channels'] = null;
    }

    if (this.editingConfiguration) {
      this.api.updateCompanyProductDeployConfiguration(this.editingConfiguration.id.toString(), body).subscribe(() => {
        this.loadConfigurations();
        this.cancelForm();
      });
    } else {
      this.api.createCompanyProductDeployConfiguration(this.data.companyProductId, body).subscribe(() => {
        this.loadConfigurations();
        this.cancelForm();
      });
    }
  }

  deleteConfiguration(config: CompanyProductDeployConfiguration): void {
    if (!confirm('¿Estás seguro de que quieres eliminar esta configuración?')) {
      return;
    }

    this.api.deleteCompanyProductDeployConfiguration(config.id.toString()).subscribe(() => {
      this.loadConfigurations();
    });
  }

  private loadConfigurations(): void {
    this.api.getCompanyProductDeployConfigurations(this.data.companyProductId, { with: 'notifiable' }).subscribe(configs => {
      this.configurations = configs;
      this.cdr.markForCheck();
    });
  }

  private setupChannelControls(): void {
    this.emailChannelControl.valueChanges.subscribe(checked => {
      const channels = this.form.get('notification_channels')?.value || [];
      if (checked && !channels.includes('email')) {
        this.form.get('notification_channels')?.setValue([...channels, 'email'], { emitEvent: false });
      } else if (!checked) {
        this.form.get('notification_channels')?.setValue(
          channels.filter((channel: string) => channel !== 'email'),
          { emitEvent: false }
        );
      }
    });

    this.slackChannelControl.valueChanges.subscribe(checked => {
      const channels = this.form.get('notification_channels')?.value || [];
      if (checked && !channels.includes('slack')) {
        this.form.get('notification_channels')?.setValue([...channels, 'slack'], { emitEvent: false });
      } else if (!checked) {
        this.form.get('notification_channels')?.setValue(
          channels.filter((channel: string) => channel !== 'slack'),
          { emitEvent: false }
        );
      }
    });

    this.teamsChannelControl.valueChanges.subscribe(checked => {
      const channels = this.form.get('notification_channels')?.value || [];
      if (checked && !channels.includes('teams')) {
        this.form.get('notification_channels')?.setValue([...channels, 'teams'], { emitEvent: false });
      } else if (!checked) {
        this.form.get('notification_channels')?.setValue(
          channels.filter((channel: string) => channel !== 'teams'),
          { emitEvent: false }
        );
      }
    });

    this.form.get('notification_channels')?.valueChanges.subscribe((channels: string[]) => {
      this.emailChannelControl.setValue((channels || []).includes('email'), { emitEvent: false });
      this.slackChannelControl.setValue((channels || []).includes('slack'), { emitEvent: false });
      this.teamsChannelControl.setValue((channels || []).includes('teams'), { emitEvent: false });
    });
  }

  private validateNotificationChannels(control: UntypedFormControl) {
    const notifiableType = this.form?.get('notifiable_type')?.value;
    const normalized = notifiableType ? notifiableType.replace(/\\\\/g, '\\') : '';

    if (normalized === 'App\\Models\\MessagingConversation' || normalized === 'App\Models\MessagingConversation') {
      return null;
    }

    if (normalized === 'App\\Models\\User' || normalized === 'App\Models\User') {
      if (!control.value || control.value.length === 0) {
        return { required: true };
      }

      return null;
    }

    return null;
  }

  private listenNotifiableTypeChanges(): void {
    this.form.get('notifiable_type')?.valueChanges.subscribe((value: string) => {
      const normalized = value ? value.replace(/\\\\/g, '\\') : '';
      this.selectedNotifiableTypeValue = normalized;
      this.selectedUser = null;
      this.userAutocompleteControl.setValue('');
      this.form.patchValue({ notifiable_id: '' });

      const channelsControl = this.form.get('notification_channels');
      if (channelsControl) {
        channelsControl.updateValueAndValidity();
      }

      this.cdr.markForCheck();
    });
  }

  private setupUserAutocomplete(): void {
    this.filteredUsers = this.userAutocompleteControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((search: string | User) => {
        if (typeof search === 'string' && search.length >= 2) {
          return this.searchUsers(search);
        }

        if (search instanceof Object && (search as User).id) {
          return of([search as User]);
        }

        return of([]);
      })
    );
  }

  private searchUsers(search: string): Observable<User[]> {
    const currentUser = this.userService.getCurrentUser();
    const isClient = this.permissions.isClient();

    if (isClient) {
      return this.api.getUsers({ search, company_id: currentUser.company_id }).pipe(switchMap(response => of(response.data || [])));
    }

    return this.api.getActiveCompanyProductMembersWithBusiness(this.data.companyProductId).pipe(
      switchMap(membersResponse => {
        const allUsers: User[] = [];

        membersResponse.bisual_users?.forEach(member => {
          if (member.user) {
            allUsers.push(member.user);
          }
        });

        membersResponse.company_users?.forEach(user => {
          allUsers.push(user);
        });

        const searchLower = search.toLowerCase();
        const filtered = allUsers.filter(user => {
          return (
            user.name?.toLowerCase().includes(searchLower) ||
            user.surnames?.toLowerCase().includes(searchLower) ||
            user.email?.toLowerCase().includes(searchLower) ||
            user.full_name?.toLowerCase().includes(searchLower)
          );
        });

        return of(filtered);
      })
    );
  }

  private listenUserSelection(): void {
    this.userAutocompleteControl.valueChanges.subscribe((value: User | string) => {
      if (value instanceof Object && (value as User).id) {
        this.selectedUser = value as User;
        this.form.patchValue({ notifiable_id: (value as User).id });
      } else if (typeof value === 'string') {
        this.selectedUser = null;
        this.form.patchValue({ notifiable_id: '' });
      }
    });
  }
}
